/* eslint react/sort-comp: 0 */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
  Button,
  Empty,
  Input,
  Popconfirm,
  Spin,
  message as antdMessage,
} from './antd';
import {
  Copy as CopyIcon,
  Sparkle,
  X as XIcon,
  List as ListIcon,
  FileText as FileTextIcon,
  WarningCircle as WarningCircleIcon,
} from '@phosphor-icons/react';
import Draggable from 'react-draggable';
import ReactMarkdown from './MemoizedMarkdown';

import {
  setAiChatVisible,
  setAiChatFeatureEnabled,
  setAiChatNearBottom,
  clearAiChatUnseenMessages,
  selectAiChatConversation,
  getAiChatConversations,
  createAiChatConversation,
  renameAiChatConversation,
  getAiChatMessages,
  sendAiChatMessage,
  retryAiChatMessage,
  deleteAiChatConversation,
  applyAiChatStreamDelta,
  resetAiChatStreamMessage,
  completeAiChatStreamMessage,
  failAiChatStreamMessage,
  setAiChatWindowGeometry,
  setAiChatSidebarCollapsed,
  startAiChatDraft,
  setAiChatDraftDocument,
  openAiChatDraftIncludePrompt,
  createAiChatDocumentConversation,
} from '../../actions/aiChat';
import { verifyFeatureAccess } from '../../global/featureFlags';
import { captureAnalytics } from '../../actions/preload';
import selectSelectedRecordDetails from '../../selectors/selectedRecordDetails';
import MarkdownRenderBoundary from './MarkdownRenderBoundary';
import GroundingFooter from './GroundingFooter';
import {
  MAX_CONTENT_LENGTH,
  MAX_ATTACHMENT_SIZE,
  ALLOWED_ATTACHMENT_TYPES,
  EMPTY_PROMPT_CHIPS,
  remarkPlugins,
  rehypePlugins,
  markdownComponents,
  formatMessageTime,
  formatFileSize,
  documentViewerPath,
  DOCUMENT_FALLBACK_TITLE,
} from './shared';
import './AiChat.scss';

const MAX_CONVERSATION_TITLE_LENGTH = 120;
const MOBILE_BREAKPOINT = 800;
const MIN_WINDOW_WIDTH = 420;
const MIN_WINDOW_HEIGHT = 350;
const SIDEBAR_AUTO_COLLAPSE_WIDTH = 560;
const RESIZE_EDGES = ['n', 's', 'e', 'w', 'ne', 'nw', 'se', 'sw'];
const STREAM_RECONCILE_INTERVAL_MS = 30000;
const STREAM_RECONCILE_TIMEOUT_MS = 90000;

const { TextArea } = Input;

const formatDateTime = (timestamp) => {
  if (!timestamp) return '';

  const value = new Date(timestamp);
  if (Number.isNaN(value.getTime())) return '';

  return value.toLocaleString();
};

const statusLabelForMessage = (messageItem) => {
  if (messageItem.status === 'failed') return 'Failed';
  if (messageItem.status === 'pending') return messageItem.role === 'assistant' ? '' : 'Sending...';
  return 'Sent';
};

const statusClassForMessage = (messageItem) => {
  if (messageItem.status === 'failed') return 'failed';
  if (messageItem.status === 'pending') return 'pending';
  return 'sent';
};

export class AiChatComponent extends Component {
  constructor(props) {
    super(props);

    this.state = {
      composerText: '',
      selectedFile: null,
      attachmentError: null,
      isDesktop: this.isDesktopViewport(),
      isRenamingConversation: false,
      renameConversationTitle: '',
      isDraggingAttachment: false,
      isResizing: false,
    };

    this.messagesContainerRef = React.createRef();
    this.fileInputRef = React.createRef();
    this.nodeRef = React.createRef();
    this.lastFeatureCheckUid = null;
    this.aiChatSubscription = null;
    this.aiChatSubscriptionSlug = null;
    this.resizeState = null;
    this.messagesAbortController = null;
    this.pendingDeltas = [];
    this.deltaRafId = null;
    this.streamReconcileIntervalId = null;
    this.reconcileStartedAt = null;
  }

  componentDidMount() {
    this.syncFeatureAccess();
    this.syncStreamSubscription();
    this.syncStreamReconciliation();
    window.addEventListener('resize', this.handleViewportResize);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleViewportResize);
    this.cleanupStreamSubscription();
    this.cleanupStreamReconciliation();
    this.endResize();
    if (this.messagesAbortController) {
      this.messagesAbortController.abort();
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevProps.user !== this.props.user) {
      this.syncFeatureAccess();
    }

    if (this.shouldBootstrap(prevProps)) {
      this.bootstrapConversations();
    }

    if (this.shouldFetchMessages(prevProps)) {
      this.fetchMessages(this.props.selectedConversationSlug);
    }

    if (prevProps.selectedConversationSlug !== this.props.selectedConversationSlug) {
      const selectedConversation = this.selectedConversation();
      this.setState({
        isRenamingConversation: false,
        renameConversationTitle: selectedConversation?.title || '',
      });
    }

    if (prevProps.messages !== this.props.messages && this.props.visible) {
      const shouldForceScroll = prevProps.selectedConversationSlug !== this.props.selectedConversationSlug;
      if (shouldForceScroll || this.props.isNearBottom) {
        this.scrollToBottom(shouldForceScroll ? 'auto' : 'smooth');
      }
    }

    if (
      prevProps.visible !== this.props.visible
      || prevProps.selectedConversationSlug !== this.props.selectedConversationSlug
      || prevProps.featureEnabled !== this.props.featureEnabled
      || prevProps.user !== this.props.user
      || prevProps.cable !== this.props.cable
      || prevState.isDesktop !== this.state.isDesktop
    ) {
      this.syncStreamSubscription();
    }

    if (
      prevProps.visible !== this.props.visible
      || prevProps.selectedConversationSlug !== this.props.selectedConversationSlug
      || prevProps.featureEnabled !== this.props.featureEnabled
      || prevProps.user !== this.props.user
      || prevProps.streamingResponse !== this.props.streamingResponse
      || prevState.isDesktop !== this.state.isDesktop
    ) {
      this.syncStreamReconciliation();
    }

    if (
      prevProps.windowSize !== this.props.windowSize
      && this.props.windowSize.width < SIDEBAR_AUTO_COLLAPSE_WIDTH
      && !this.props.sidebarCollapsed
    ) {
      this.props.setAiChatSidebarCollapsed(true);
    }
  }

  isDesktopViewport() {
    return window.innerWidth >= MOBILE_BREAKPOINT;
  }

  handleViewportResize = () => {
    const nextIsDesktop = this.isDesktopViewport();
    if (nextIsDesktop !== this.state.isDesktop) {
      this.setState({ isDesktop: nextIsDesktop });
    }

    if (!nextIsDesktop && this.props.visible) {
      this.props.setAiChatVisible(false);
    }

    if (nextIsDesktop && this.props.windowPosition) {
      const { windowPosition, windowSize } = this.props;
      const maxX = window.innerWidth - windowSize.width;
      const maxY = window.innerHeight - windowSize.height;
      const clampedX = Math.max(0, Math.min(windowPosition.x, maxX));
      const clampedY = Math.max(0, Math.min(windowPosition.y, maxY));
      if (clampedX !== windowPosition.x || clampedY !== windowPosition.y) {
        this.props.setAiChatWindowGeometry({ position: { x: clampedX, y: clampedY } });
      }
    }
  };

  cleanupStreamSubscription() {
    if (this.aiChatSubscription) {
      this.aiChatSubscription.unsubscribe();
      this.aiChatSubscription = null;
      this.aiChatSubscriptionSlug = null;
    }
    this.pendingDeltas = [];
    if (this.deltaRafId) {
      cancelAnimationFrame(this.deltaRafId);
      this.deltaRafId = null;
    }
  }

  shouldReconcileStream() {
    return (
      this.canShowWidget()
      && this.props.visible
      && Boolean(this.props.selectedConversationSlug)
      && this.props.streamingResponse
    );
  }

  reconcileMessages = () => {
    const { selectedConversationSlug, failAiChatStreamMessage: failStreamMessage } = this.props;
    if (!selectedConversationSlug || document.visibilityState !== 'visible') return;

    if (this.reconcileStartedAt
      && Date.now() - this.reconcileStartedAt > STREAM_RECONCILE_TIMEOUT_MS) {
      this.cleanupStreamReconciliation();
      const pendingMsg = this.props.messages.find(
        (m) => m.role === 'assistant' && m.status === 'pending',
      );
      if (pendingMsg) {
        failStreamMessage(selectedConversationSlug, pendingMsg, 'Response timed out');
      }
      return;
    }

    this.fetchMessages(selectedConversationSlug);
  };

  handleVisibilityChangeForReconcile = () => {
    if (document.visibilityState === 'visible') {
      this.reconcileMessages();
    }
  };

  syncStreamReconciliation() {
    if (!this.shouldReconcileStream()) {
      this.cleanupStreamReconciliation();
      return;
    }

    if (this.streamReconcileIntervalId) {
      return;
    }

    this.reconcileStartedAt = Date.now();
    window.addEventListener('focus', this.reconcileMessages);
    document.addEventListener('visibilitychange', this.handleVisibilityChangeForReconcile);
    this.streamReconcileIntervalId = window.setInterval(
      this.reconcileMessages,
      STREAM_RECONCILE_INTERVAL_MS,
    );
  }

  cleanupStreamReconciliation() {
    window.removeEventListener('focus', this.reconcileMessages);
    document.removeEventListener('visibilitychange', this.handleVisibilityChangeForReconcile);
    if (this.streamReconcileIntervalId) {
      window.clearInterval(this.streamReconcileIntervalId);
      this.streamReconcileIntervalId = null;
    }
  }

  computeDefaultPosition() {
    const { windowSize } = this.props;
    const x = Math.max(0, window.innerWidth - windowSize.width - 24);
    const y = Math.max(80, Math.round((window.innerHeight - windowSize.height) / 2));
    return { x, y };
  }

  startResize = (edge, e) => {
    e.preventDefault();
    e.stopPropagation();
    const { windowPosition, windowSize } = this.props;
    const pos = windowPosition || this.computeDefaultPosition();
    this.resizeState = {
      edge,
      startMouseX: e.clientX,
      startMouseY: e.clientY,
      startX: pos.x,
      startY: pos.y,
      startWidth: windowSize.width,
      startHeight: windowSize.height,
    };
    this.setState({ isResizing: true });
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', this.onResizeMove);
    document.addEventListener('mouseup', this.endResize);
  };

  onResizeMove = (e) => {
    if (!this.resizeState) return;
    const {
      edge, startMouseX, startMouseY,
      startX, startY, startWidth, startHeight,
    } = this.resizeState;
    const dx = e.clientX - startMouseX;
    const dy = e.clientY - startMouseY;

    let nextX = startX;
    let nextY = startY;
    let nextW = startWidth;
    let nextH = startHeight;

    if (edge.includes('e')) {
      nextW = Math.max(MIN_WINDOW_WIDTH, startWidth + dx);
    }
    if (edge.includes('w')) {
      const widthDelta = Math.min(dx, startWidth - MIN_WINDOW_WIDTH);
      nextW = startWidth - widthDelta;
      nextX = startX + widthDelta;
    }
    if (edge.includes('s')) {
      nextH = Math.max(MIN_WINDOW_HEIGHT, startHeight + dy);
    }
    if (edge.includes('n')) {
      const heightDelta = Math.min(dy, startHeight - MIN_WINDOW_HEIGHT);
      nextH = startHeight - heightDelta;
      nextY = startY + heightDelta;
    }

    nextW = Math.min(nextW, window.innerWidth);
    nextH = Math.min(nextH, window.innerHeight);
    nextX = Math.max(0, Math.min(nextX, window.innerWidth - nextW));
    nextY = Math.max(0, Math.min(nextY, window.innerHeight - nextH));

    this.props.setAiChatWindowGeometry({
      position: { x: nextX, y: nextY },
      size: { width: nextW, height: nextH },
    });
  };

  endResize = () => {
    if (!this.resizeState) return;
    this.resizeState = null;
    this.setState({ isResizing: false });
    document.body.style.userSelect = '';
    document.removeEventListener('mousemove', this.onResizeMove);
    document.removeEventListener('mouseup', this.endResize);
  };

  syncStreamSubscription() {
    const {
      cable,
      selectedConversationSlug,
      visible,
    } = this.props;

    if (!this.canShowWidget() || !visible || !selectedConversationSlug || !cable) {
      this.cleanupStreamSubscription();
      return;
    }

    if (this.aiChatSubscription && this.aiChatSubscriptionSlug === selectedConversationSlug) {
      return;
    }

    this.cleanupStreamSubscription();

    this.aiChatSubscription = cable.subscriptions.create(
      {
        channel: 'AiChatChannel',
        conversation_slug: selectedConversationSlug,
      },
      {
        received: (payload) => this.handleStreamEvent(payload),
      },
    );
    this.aiChatSubscriptionSlug = selectedConversationSlug;
  }

  handleStreamEvent(payload) {
    const {
      completeAiChatStreamMessage: completeStreamMessage,
      failAiChatStreamMessage: failStreamMessage,
      resetAiChatStreamMessage: resetStreamMessage,
      getAiChatConversations: loadConversations,
    } = this.props;

    if (!payload?.type) return;

    switch (payload.type) {
      case 'ai_chat.stream.delta':
        this.pendingDeltas.push({
          conversationSlug: payload.conversation_slug,
          assistantMessageId: payload.assistant_message_id,
          delta: payload.delta,
        });
        if (!this.deltaRafId) {
          this.deltaRafId = requestAnimationFrame(() => this.flushPendingDeltas());
        }
        break;
      case 'ai_chat.stream.reset':
        this.pendingDeltas = this.pendingDeltas.filter((pendingDelta) => (
          pendingDelta.conversationSlug !== payload.conversation_slug
          || String(pendingDelta.assistantMessageId) !== String(payload.assistant_message_id)
        ));
        resetStreamMessage(payload.conversation_slug, payload.assistant_message_id);
        break;
      case 'ai_chat.stream.completed':
        this.pendingDeltas = [];
        if (this.deltaRafId) {
          cancelAnimationFrame(this.deltaRafId);
          this.deltaRafId = null;
        }
        completeStreamMessage(payload.conversation_slug, payload.assistant_message);
        loadConversations();
        break;
      case 'ai_chat.stream.failed':
        this.pendingDeltas = [];
        if (this.deltaRafId) {
          cancelAnimationFrame(this.deltaRafId);
          this.deltaRafId = null;
        }
        failStreamMessage(payload.conversation_slug, payload.assistant_message, payload.error);
        loadConversations();
        break;
      default:
        break;
    }
  }

  flushPendingDeltas() {
    this.deltaRafId = null;
    const deltas = this.pendingDeltas;
    this.pendingDeltas = [];
    if (deltas.length === 0) return;

    const { applyAiChatStreamDelta: applyStreamDelta } = this.props;
    const grouped = {};
    deltas.forEach(({ conversationSlug, assistantMessageId, delta }) => {
      const key = `${conversationSlug}:${assistantMessageId}`;
      if (!grouped[key]) {
        grouped[key] = { conversationSlug, assistantMessageId, delta: '' };
      }
      grouped[key].delta += delta || '';
    });

    Object.values(grouped).forEach(({ conversationSlug, assistantMessageId, delta }) => {
      applyStreamDelta(conversationSlug, assistantMessageId, delta);
    });
  }

  shouldBootstrap(prevProps) {
    return (
      this.canShowWidget()
      && this.props.visible
      && (
        prevProps.visible !== this.props.visible
        || (prevProps.featureEnabled !== this.props.featureEnabled && this.props.featureEnabled)
      )
    );
  }

  shouldFetchMessages(prevProps) {
    return (
      this.canShowWidget()
      && this.props.visible
      && this.props.selectedConversationSlug
      && (
        prevProps.selectedConversationSlug !== this.props.selectedConversationSlug
        || prevProps.visible !== this.props.visible
        || (prevProps.featureEnabled !== this.props.featureEnabled && this.props.featureEnabled)
      )
    );
  }

  canShowWidget() {
    return Boolean(this.props.user) && this.props.featureEnabled && this.state.isDesktop;
  }

  fetchMessages(conversationSlug) {
    const { getAiChatMessages: loadMessages } = this.props;
    if (!conversationSlug) return;

    if (this.messagesAbortController) {
      this.messagesAbortController.abort();
    }
    this.messagesAbortController = new AbortController();

    loadMessages(
      conversationSlug,
      { signal: this.messagesAbortController.signal },
    );
  }

  selectedConversation() {
    return this.props.conversations.find((conversation) => (
      conversation.slug === this.props.selectedConversationSlug
    )) || null;
  }

  async syncFeatureAccess() {
    const {
      user,
      setAiChatFeatureEnabled: setFeatureEnabled,
      setAiChatVisible: setVisible,
    } = this.props;

    if (!user) {
      this.lastFeatureCheckUid = null;
      setVisible(false);
      setFeatureEnabled(false);
      return;
    }

    const userUid = user.uid;
    this.lastFeatureCheckUid = userUid;

    const enabled = await verifyFeatureAccess('medsender_gpt');
    if (this.props.user && this.props.user.uid === userUid && this.lastFeatureCheckUid === userUid) {
      setFeatureEnabled(Boolean(enabled));
    }
  }

  // Loads the history list only. ENG-3815: it never selects a conversation; picking the
  // newest one is what used to open a chat about a different document.
  bootstrapConversations() {
    return this.props.getAiChatConversations();
  }

  async toggleVisibility() {
    const {
      visible,
      setAiChatVisible: setVisible,
    } = this.props;

    if (visible) {
      setVisible(false);
      return;
    }

    captureAnalytics('medsender_gpt_opened', { surface: 'launcher', on_document: Boolean(this.viewedDocument()) });
    setVisible(true);
  }

  // Starts a draft; the chat is saved only when its first message is sent.
  handleCreateConversation() {
    captureAnalytics('medsender_gpt_new_chat_clicked');
    this.setState({
      isRenamingConversation: false,
      renameConversationTitle: '',
    });
    this.props.startAiChatDraft();
  }

  // The record open in the viewer, or null. An ATA fax has no document to read.
  viewedDocument() {
    const { viewedFileToken, viewedDocumentTitle, viewedDocumentViewerPath } = this.props;
    if (!viewedFileToken) return null;

    return { fileToken: viewedFileToken, title: viewedDocumentTitle, viewerPath: viewedDocumentViewerPath };
  }

  linkedFileToken() {
    if (!this.props.selectedConversationSlug) return this.props.draftDocument?.fileToken || null;

    return this.selectedConversation()?.record_file_token || null;
  }

  // Links the chat to the record on screen: resumes this user's existing chat about it
  // (one per document), otherwise starts a draft linked to it.
  handleChatAboutViewedDocument(source) {
    const viewedDocument = this.viewedDocument();
    if (!viewedDocument) return;

    const existing = this.props.conversations.find((conversation) => (
      conversation.record_file_token === viewedDocument.fileToken
    ));
    captureAnalytics('medsender_gpt_document_chat_chosen', { source, resumed: Boolean(existing) });
    if (existing) {
      this.props.selectAiChatConversation(existing.slug);
      return;
    }
    this.props.startAiChatDraft(viewedDocument);
  }

  handleAskSomethingElse() {
    captureAnalytics('medsender_gpt_document_chat_declined');
    this.props.setAiChatDraftDocument(null);
  }

  async handleIncludePromptAnswer(include) {
    const message = this.composerMessage();
    if (!message) return;

    const linkedDocument = include ? this.viewedDocument() : null;
    captureAnalytics('medsender_gpt_include_document_answered', { include });
    this.props.setAiChatDraftDocument(linkedDocument);
    const conversationSlug = await this.createDraftConversation(linkedDocument);
    if (conversationSlug) await this.sendToConversation(conversationSlug, message);
  }

  // Saves the draft as a real conversation and returns its slug, or null after telling
  // the user it failed.
  async createDraftConversation(linkedDocument) {
    const {
      createAiChatConversation: createConversation,
      createAiChatDocumentConversation: createDocumentConversation,
      selectAiChatConversation: chooseConversation,
    } = this.props;

    if (linkedDocument) {
      const result = await createDocumentConversation(
        linkedDocument.fileToken,
        linkedDocument.title || 'Document Chat',
        linkedDocument.viewerPath,
      );
      // One chat per document: the server hands back the existing one.
      if (result?.isExisting) {
        antdMessage.info(`Continued your existing chat about ${linkedDocument.title || DOCUMENT_FALLBACK_TITLE}.`);
      }
      if (result?.conversationSlug) return result.conversationSlug;
    } else {
      const created = await createConversation();
      const createdSlug = created?.conversation?.slug;
      if (createdSlug) {
        chooseConversation(createdSlug);
        return createdSlug;
      }
    }

    antdMessage.error('Failed to create conversation. Please try again.');
    return null;
  }

  async handleDeleteConversation() {
    const {
      selectedConversationSlug,
      deleteAiChatConversation: destroyConversation,
    } = this.props;

    if (!selectedConversationSlug) return;

    const deleted = await destroyConversation(selectedConversationSlug);
    if (deleted) {
      await this.bootstrapConversations();
    }
  }

  startConversationRename() {
    const selectedConversation = this.selectedConversation();
    this.setState({
      isRenamingConversation: true,
      renameConversationTitle: selectedConversation?.title || '',
    });
  }

  cancelConversationRename() {
    const selectedConversation = this.selectedConversation();
    this.setState({
      isRenamingConversation: false,
      renameConversationTitle: selectedConversation?.title || '',
    });
  }

  async submitConversationRename() {
    const {
      selectedConversationSlug,
      renameAiChatConversation: renameConversation,
    } = this.props;
    const { renameConversationTitle } = this.state;

    if (!selectedConversationSlug) return;

    const normalizedTitle = renameConversationTitle.trim();
    if (!normalizedTitle) {
      antdMessage.error('Conversation title is required.');
      return;
    }
    if (normalizedTitle.length > MAX_CONVERSATION_TITLE_LENGTH) {
      antdMessage.error(`Conversation title must be ${MAX_CONVERSATION_TITLE_LENGTH} characters or less.`);
      return;
    }

    const response = await renameConversation(selectedConversationSlug, normalizedTitle);
    if (!response) return;

    this.setState({
      isRenamingConversation: false,
      renameConversationTitle: response.conversation.title,
    });
  }

  validateAttachment(file) {
    if (!file) return null;

    if (!ALLOWED_ATTACHMENT_TYPES.includes(file.type)) {
      return 'Unsupported attachment type.';
    }

    if (file.size > MAX_ATTACHMENT_SIZE) {
      return 'Attachment is too large. Maximum size is 20MB.';
    }

    return null;
  }

  applyAttachmentSelection(file) {
    if (!file) return;

    const validationError = this.validateAttachment(file);
    if (validationError) {
      this.setState({ attachmentError: validationError });
      antdMessage.error(validationError);
      if (this.fileInputRef.current) this.fileInputRef.current.value = '';
      return;
    }

    this.setState({
      selectedFile: file,
      attachmentError: null,
      isDraggingAttachment: false,
    });
  }

  handleFileSelection(event) {
    const file = event.target.files?.[0];
    this.applyAttachmentSelection(file);
  }

  handlePasteComposer(event) {
    const files = Array.from(event.clipboardData?.files || []);
    if (files.length === 0) return;

    event.preventDefault();
    this.applyAttachmentSelection(files[0]);
  }

  handleAttachmentDragOver(event) {
    event.preventDefault();
    if (!this.state.isDraggingAttachment) {
      this.setState({ isDraggingAttachment: true });
    }
  }

  handleAttachmentDragLeave(event) {
    event.preventDefault();
    if (this.state.isDraggingAttachment) {
      this.setState({ isDraggingAttachment: false });
    }
  }

  handleAttachmentDrop(event) {
    event.preventDefault();
    const droppedFile = event.dataTransfer?.files?.[0];
    this.applyAttachmentSelection(droppedFile);
  }

  clearSelectedAttachment() {
    this.setState({
      selectedFile: null,
      attachmentError: null,
    });
    if (this.fileInputRef.current) this.fileInputRef.current.value = '';
  }

  // The composer's message, or null (after telling the user why) when it can't be sent.
  composerMessage() {
    const { sendingMessage, streamingResponse, loadingConversations } = this.props;
    const { composerText, selectedFile } = this.state;

    if (sendingMessage || streamingResponse || loadingConversations) return null;

    const normalizedContent = composerText.trim();
    if (!normalizedContent && !selectedFile) {
      antdMessage.error('Type a message or attach a file.');
      return null;
    }

    if (normalizedContent.length > MAX_CONTENT_LENGTH) {
      antdMessage.error(`Message content must be ${MAX_CONTENT_LENGTH} characters or less.`);
      return null;
    }

    return { content: normalizedContent, attachment: selectedFile };
  }

  async handleSendMessage() {
    const { selectedConversationSlug, draftDocument } = this.props;
    const message = this.composerMessage();
    if (!message) return;

    if (selectedConversationSlug) {
      await this.sendToConversation(selectedConversationSlug, message);
      return;
    }

    // A draft on a document whose question was skipped asks once before saving.
    if (draftDocument === undefined && this.viewedDocument()) {
      this.props.openAiChatDraftIncludePrompt();
      return;
    }

    const conversationSlug = await this.createDraftConversation(draftDocument || null);
    if (conversationSlug) await this.sendToConversation(conversationSlug, message);
  }

  async sendToConversation(conversationSlug, message) {
    const {
      sendAiChatMessage: sendMessage,
      getAiChatConversations: loadConversations,
      setAiChatNearBottom: setNearBottom,
      clearAiChatUnseenMessages: clearUnseen,
    } = this.props;

    setNearBottom(true);
    clearUnseen();

    const response = await sendMessage(conversationSlug, message);
    if (!response) return;

    this.setState({
      composerText: '',
      selectedFile: null,
      attachmentError: null,
    });
    if (this.fileInputRef.current) this.fileInputRef.current.value = '';
    await loadConversations();
  }

  async handleRetryMessage(failedMessageId) {
    const {
      selectedConversationSlug,
      retryAiChatMessage: retryMessage,
      getAiChatConversations: loadConversations,
    } = this.props;

    if (!selectedConversationSlug || !failedMessageId) return;

    const response = await retryMessage(selectedConversationSlug, failedMessageId);
    if (!response) return;

    await loadConversations();
  }

  handleComposerKeyDown(event) {
    if (event.key !== 'Enter' || event.isComposing) return;
    if (event.shiftKey) return;

    event.preventDefault();
    this.handleSendMessage();
  }

  async handleCopyMessage(messageText) {
    if (!messageText) return;

    try {
      await navigator.clipboard.writeText(messageText);
      antdMessage.success('Copied');
    } catch {
      antdMessage.error('Failed to copy message.');
    }
  }

  handleMessagesScroll() {
    const container = this.messagesContainerRef.current;
    if (!container) return;

    const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
    const isNearBottom = distanceFromBottom <= 96;

    if (isNearBottom !== this.props.isNearBottom) {
      this.props.setAiChatNearBottom(isNearBottom);
    }

    if (isNearBottom && this.props.hasUnseenNewMessages) {
      this.props.clearAiChatUnseenMessages();
    }
  }

  scrollToBottom(behavior = 'smooth') {
    const container = this.messagesContainerRef.current;
    if (!container) return;

    container.scrollTo({
      top: container.scrollHeight,
      behavior,
    });
    this.props.setAiChatNearBottom(true);
    this.props.clearAiChatUnseenMessages();
  }

  fillPromptChip(promptText) {
    this.setState({ composerText: promptText });
  }

  renderConversationList() {
    const {
      conversations,
      loadingConversations,
      selectedConversationSlug,
      selectAiChatConversation: chooseConversation,
    } = this.props;

    return (
      <div className="ai-chat-conversations">
        <div className="ai-chat-list-actions">
          <Button type="primary" size="small" onClick={() => this.handleCreateConversation()}>
            New Chat
          </Button>
        </div>
        <div className="ai-chat-list">
          {loadingConversations && conversations.length === 0 ? (
            <div className="ai-chat-loading">
              <Spin size="small" />
            </div>
          ) : null}
          {conversations.map((conversation) => {
            const conversationTime = formatDateTime(conversation.last_message_at);

            return (
              <div
                key={conversation.slug}
                className={`ai-chat-list-item ${selectedConversationSlug === conversation.slug ? 'active' : ''}`}
                role="button"
                tabIndex={0}
                onClick={() => chooseConversation(conversation.slug)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    chooseConversation(conversation.slug);
                  }
                }}
              >
                <div className="ai-chat-list-item-title">
                  {conversation.record_file_token ? <FileTextIcon size={13} className="ai-chat-list-item-doc-icon" /> : null}
                  {conversation.title}
                </div>
                <div className="ai-chat-list-item-preview">{conversation.last_message_preview || 'No messages yet'}</div>
                {conversationTime ? <div className="ai-chat-list-item-time">{conversationTime}</div> : null}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  renderAttachmentsForMessage(messageItem) {
    if (!messageItem.attachments || messageItem.attachments.length === 0) return null;

    return (
      <div className="ai-chat-attachments">
        {messageItem.attachments.map((attachment) => (
          <div key={attachment.file_token}>
            {attachment.download_url ? (
              <a href={attachment.download_url} target="_blank" rel="noreferrer">
                {attachment.original_filename}
              </a>
            ) : (
              <span>{attachment.original_filename}</span>
            )}
          </div>
        ))}
      </div>
    );
  }

  renderMessageBody(messageItem) {
    const isPendingAssistant = messageItem.role === 'assistant' && messageItem.status === 'pending';

    if (messageItem.role === 'assistant') {
      if (isPendingAssistant && !messageItem.content) {
        return (
          <div className="ai-chat-thinking">
            <span className="ai-chat-thinking-dot" />
            <span className="ai-chat-thinking-dot" />
            <span className="ai-chat-thinking-dot" />
            <span className="ai-chat-thinking-label">Thinking</span>
          </div>
        );
      }

      const markdownContent = messageItem.content || '';
      return (
        <MarkdownRenderBoundary markdownContent={markdownContent}>
          <ReactMarkdown
            className="ai-chat-markdown"
            remarkPlugins={remarkPlugins}
            rehypePlugins={rehypePlugins}
            components={markdownComponents}
          >
            {markdownContent}
          </ReactMarkdown>
        </MarkdownRenderBoundary>
      );
    }

    return messageItem.content || '';
  }

  renderMessageActions(messageItem, latestMessageId) {
    const isLatestMessage = String(latestMessageId) === String(messageItem.id);
    const isRetrying = this.props.retryingMessageIds.includes(messageItem.id);
    const showCopy = messageItem.status === 'completed' && messageItem.content;
    const showRetry = messageItem.role === 'assistant'
      && messageItem.status === 'failed'
      && isLatestMessage;

    if (!showCopy && !showRetry) return null;

    return (
      <div className="ai-chat-message-actions">
        {showCopy ? (
          <Button
            size="small"
            type="link"
            aria-label="Copy"
            icon={<CopyIcon size={14} />}
            onClick={() => this.handleCopyMessage(messageItem.content)}
          />
        ) : null}
        {showRetry ? (
          <Button
            size="small"
            type="link"
            loading={isRetrying}
            onClick={() => this.handleRetryMessage(messageItem.id)}
          >
            Retry
          </Button>
        ) : null}
      </div>
    );
  }

  // ENG-3815: a new chat opened on a document asks whether it is about that document.
  renderDocumentQuestion() {
    const { selectedConversationSlug, draftDocument } = this.props;
    const viewedDocument = this.viewedDocument();
    if (selectedConversationSlug || draftDocument !== undefined || !viewedDocument) return null;

    return (
      <div className="ai-chat-document-question">
        <div className="ai-chat-document-question-text">
          {'You\'re viewing '}
          <strong>{viewedDocument.title || DOCUMENT_FALLBACK_TITLE}</strong>
        </div>
        <div className="ai-chat-document-question-actions">
          <Button type="primary" size="small" onClick={() => this.handleChatAboutViewedDocument('question')}>
            Chat about this document
          </Button>
          <Button size="small" onClick={() => this.handleAskSomethingElse()}>
            Ask something else
          </Button>
        </div>
      </div>
    );
  }

  // Shown when the chat is linked to a different record than the one on screen.
  renderDocumentMismatch() {
    const viewedDocument = this.viewedDocument();
    const linkedFileToken = this.linkedFileToken();
    if (!viewedDocument || !linkedFileToken || linkedFileToken === viewedDocument.fileToken) return null;

    return (
      <div className="ai-chat-document-mismatch" role="status">
        <WarningCircleIcon size={16} weight="fill" className="ai-chat-document-mismatch-icon" />
        <span>
          {'This chat is about a different document. You\'re viewing '}
          <strong>{viewedDocument.title || DOCUMENT_FALLBACK_TITLE}</strong>
          .
        </span>
        <Button size="small" onClick={() => this.handleChatAboutViewedDocument('mismatch')}>
          Chat about this document
        </Button>
      </div>
    );
  }

  // The document the chat reads, pinned above the input.
  renderDocumentChip() {
    const linkedFileToken = this.linkedFileToken();
    if (!linkedFileToken) return null;

    const viewedDocument = this.viewedDocument();
    let label = 'Linked document';
    if (!this.props.selectedConversationSlug) {
      label = this.props.draftDocument.title || DOCUMENT_FALLBACK_TITLE;
    } else if (viewedDocument?.fileToken === linkedFileToken) {
      label = viewedDocument.title || DOCUMENT_FALLBACK_TITLE;
    }

    return (
      <div className="ai-chat-document-chip" title={label}>
        <FileTextIcon size={13} />
        <span className="ai-chat-document-chip-label">{label}</span>
      </div>
    );
  }

  renderIncludePrompt() {
    const viewedDocument = this.viewedDocument();
    const { selectedConversationSlug, draftIncludePromptOpen } = this.props;
    if (selectedConversationSlug || !draftIncludePromptOpen || !viewedDocument) return null;

    return (
      <div className="ai-chat-include-prompt" role="alertdialog" aria-label="Include this document?">
        <span>
          {'Include '}
          <strong>{viewedDocument.title || DOCUMENT_FALLBACK_TITLE}</strong>
          {' in this chat?'}
        </span>
        <div className="ai-chat-include-prompt-actions">
          <Button type="primary" size="small" onClick={() => this.handleIncludePromptAnswer(true)}>
            Include
          </Button>
          <Button size="small" onClick={() => this.handleIncludePromptAnswer(false)}>
            Send without
          </Button>
        </div>
      </div>
    );
  }

  renderEmptyState() {
    const chips = EMPTY_PROMPT_CHIPS;
    const description = 'Start a new conversation.';

    return (
      <div className="ai-chat-empty">
        {this.renderDocumentQuestion()}
        <Empty description={description} />
        <div className="ai-chat-empty-prompts">
          {chips.map((prompt) => (
            <Button
              key={prompt}
              className="ai-chat-prompt-chip"
              onClick={() => this.fillPromptChip(prompt)}
            >
              {prompt}
            </Button>
          ))}
        </div>
      </div>
    );
  }

  renderMessages() {
    const { messages, loadingMessages, sendingMessage, streamingResponse } = this.props;
    const latestMessageId = messages[messages.length - 1]?.id;
    const isWaitingForResponse = sendingMessage || streamingResponse;

    if (loadingMessages && messages.length === 0) {
      return (
        <div className="ai-chat-messages ai-chat-loading">
          <Spin />
        </div>
      );
    }

    if (messages.length === 0) {
      return (
        <div className="ai-chat-messages">
          {this.renderEmptyState()}
        </div>
      );
    }

    return (
      <div
        ref={this.messagesContainerRef}
        className="ai-chat-messages"
        onScroll={() => this.handleMessagesScroll()}
      >
        {messages.map((messageItem) => {
          if (messageItem.role === 'assistant' && messageItem.status === 'pending' && !messageItem.content && !isWaitingForResponse) {
            return null;
          }
          const statusLabel = statusLabelForMessage(messageItem);

          return (
            <div className={`ai-chat-message ${messageItem.role}`} key={messageItem.id}>
              <div className="ai-chat-bubble-row">
                <div className="ai-chat-bubble">
                  {this.renderMessageBody(messageItem)}
                </div>
                {this.renderMessageActions(messageItem, latestMessageId)}
              </div>
              <GroundingFooter grounding={messageItem.grounding} />
              <div className="ai-chat-message-meta">
                {statusLabel ? (
                  <span className={`ai-chat-status-pill ${statusClassForMessage(messageItem)}`}>
                    {statusLabel}
                  </span>
                ) : null}
                <span className="ai-chat-message-time">{formatMessageTime(messageItem.created_at)}</span>
              </div>
              {this.renderAttachmentsForMessage(messageItem)}
            </div>
          );
        })}
      </div>
    );
  }

  renderComposer() {
    const { sendingMessage, streamingResponse, loadingConversations } = this.props;
    const {
      composerText,
      selectedFile,
      attachmentError,
      isDraggingAttachment,
    } = this.state;
    const sendDisabled = sendingMessage || streamingResponse || loadingConversations;

    return (
      <div
        className={`ai-chat-composer ${isDraggingAttachment ? 'dragging' : ''}`}
        onDragOver={(event) => this.handleAttachmentDragOver(event)}
        onDragLeave={(event) => this.handleAttachmentDragLeave(event)}
        onDrop={(event) => this.handleAttachmentDrop(event)}
      >
        {this.renderDocumentChip()}
        <TextArea
          value={composerText}
          autoSize={{ minRows: 2, maxRows: 8 }}
          placeholder="Ask MedsenderGPT..."
          maxLength={MAX_CONTENT_LENGTH}
          onChange={(event) => this.setState({ composerText: event.target.value })}
          onKeyDown={(event) => this.handleComposerKeyDown(event)}
          onPaste={(event) => this.handlePasteComposer(event)}
        />
        <div className="ai-chat-composer-actions">
          <div className="ai-chat-left-actions">
            <input
              ref={this.fileInputRef}
              type="file"
              accept=".pdf,image/png,image/jpeg,image/webp,text/plain"
              onChange={(event) => this.handleFileSelection(event)}
              style={{ display: 'none' }}
            />
            <Button size="small" onClick={() => this.fileInputRef.current?.click()}>
              Attach File
            </Button>
            <span className="ai-chat-drag-hint">Drop or paste a file/image</span>
            {selectedFile ? (
              <div className="ai-chat-selected-file">
                <span>{selectedFile.name}</span>
                <span>{formatFileSize(selectedFile.size)}</span>
                <Button
                  size="small"
                  type="link"
                  onClick={() => this.clearSelectedAttachment()}
                >
                  Remove
                </Button>
              </div>
            ) : null}
          </div>
          <Button
            type="primary"
            disabled={sendDisabled}
            loading={sendingMessage || streamingResponse}
            onClick={() => this.handleSendMessage()}
          >
            {streamingResponse ? 'Waiting...' : 'Send'}
          </Button>
        </div>
        {attachmentError ? <div className="ai-chat-attachment-error">{attachmentError}</div> : null}
      </div>
    );
  }

  renderPanelHeader() {
    const {
      selectedConversationSlug,
      retryingMessageIds,
      messages,
    } = this.props;
    const {
      isRenamingConversation,
      renameConversationTitle,
    } = this.state;

    const selectedConversation = this.selectedConversation();
    const title = selectedConversation?.title || 'New Chat';
    const latestMessage = messages[messages.length - 1];
    const showRetryingState = latestMessage && retryingMessageIds.includes(latestMessage.id);

    return (
      <div className="ai-chat-panel-header">
        <div className="ai-chat-panel-title-wrap">
          {isRenamingConversation ? (
            <div className="ai-chat-rename-row">
              <Input
                value={renameConversationTitle}
                maxLength={MAX_CONVERSATION_TITLE_LENGTH}
                onChange={(event) => this.setState({ renameConversationTitle: event.target.value })}
                onPressEnter={() => this.submitConversationRename()}
              />
              <Button size="small" type="primary" onClick={() => this.submitConversationRename()}>Save</Button>
              <Button size="small" onClick={() => this.cancelConversationRename()}>Cancel</Button>
            </div>
          ) : (
            <div className="ai-chat-panel-title">{title}</div>
          )}
          {(() => {
            const recordViewerPath = selectedConversation?.record_viewer_path;
            const viewerHref = (recordViewerPath && !recordViewerPath.startsWith('//'))
              ? recordViewerPath
              : (selectedConversation?.record_file_token
                && `/app/view/${selectedConversation.record_file_token}`);
            return viewerHref ? (
              <a className="ai-chat-panel-record-link" href={viewerHref}>
                <FileTextIcon size={13} />
                View linked record
              </a>
            ) : null;
          })()}
          {showRetryingState ? <div className="ai-chat-panel-subtitle">Retrying latest failed response...</div> : null}
        </div>
        <div className="ai-chat-header-actions">
          {!isRenamingConversation ? (
            <Button size="small" onClick={() => this.startConversationRename()} disabled={!selectedConversationSlug}>
              Rename
            </Button>
          ) : null}
          <Popconfirm
            placement="left"
            title="Delete this conversation?"
            okText="Delete"
            cancelText="Cancel"
            onConfirm={() => this.handleDeleteConversation()}
            disabled={!selectedConversationSlug}
            zIndex={1310}
          >
            <Button
              danger
              size="small"
              disabled={!selectedConversationSlug}
            >
              Delete
            </Button>
          </Popconfirm>
        </div>
      </div>
    );
  }

  renderResizeHandles() {
    return RESIZE_EDGES.map((edge) => (
      <div
        key={edge}
        role="presentation"
        className={`ai-chat-resize-handle ai-chat-resize-${edge}`}
        onMouseDown={(e) => this.startResize(edge, e)}
      />
    ));
  }

  render() {
    const {
      visible,
      error,
      hasUnseenNewMessages,
      windowPosition,
      windowSize,
      sidebarCollapsed,
      viewerChatVisible,
    } = this.props;
    const canShowWidget = this.canShowWidget();

    if (!canShowWidget) return null;

    const position = windowPosition || this.computeDefaultPosition();

    return (
      <>
        <Button
          type="primary"
          className="ai-chat-toggle"
          title="Open MedsenderGPT"
          aria-label="Open MedsenderGPT"
          onClick={() => this.toggleVisibility()}
        >
          <Sparkle size={26} weight="fill" />
        </Button>
        {visible && !viewerChatVisible && (
          <Draggable
            handle=".ai-chat-title-bar"
            bounds="body"
            position={position}
            onStop={(e, data) => this.props.setAiChatWindowGeometry({ position: { x: data.x, y: data.y } })}
            disabled={this.state.isResizing}
            nodeRef={this.nodeRef}
          >
            <div
              ref={this.nodeRef}
              className="ai-chat-floating-window"
              style={{ width: windowSize.width, height: windowSize.height }}
            >
              {this.renderResizeHandles()}
              <div className="ai-chat-title-bar">
                <span className="ai-chat-title-bar-label">MedsenderGPT</span>
                <div className="ai-chat-title-bar-actions">
                  <Button
                    size="small"
                    type="text"
                    className="ai-chat-title-bar-btn"
                    aria-label="Toggle conversations"
                    onClick={() => this.props.setAiChatSidebarCollapsed(!sidebarCollapsed)}
                  >
                    <ListIcon size={16} />
                  </Button>
                  <Button
                    size="small"
                    type="text"
                    className="ai-chat-title-bar-btn"
                    aria-label="Close MedsenderGPT"
                    onClick={() => this.props.setAiChatVisible(false)}
                  >
                    <XIcon size={16} />
                  </Button>
                </div>
              </div>
              <div className="ai-chat-body">
                <div className="ai-chat-layout">
                  {!sidebarCollapsed && this.renderConversationList()}
                  <div className="ai-chat-panel">
                    {this.renderPanelHeader()}
                    {error ? <div className="ai-chat-error">{error}</div> : null}
                    {this.renderDocumentMismatch()}
                    {this.renderMessages()}
                    {hasUnseenNewMessages ? (
                      <div className="ai-chat-jump-row">
                        <Button size="small" onClick={() => this.scrollToBottom()}>
                          Jump to latest
                        </Button>
                      </div>
                    ) : null}
                    {this.renderIncludePrompt()}
                    {this.renderComposer()}
                  </div>
                </div>
              </div>
            </div>
          </Draggable>
        )}
      </>
    );
  }
}

AiChatComponent.propTypes = {
  user: PropTypes.shape({
    uid: PropTypes.string,
  }),
  visible: PropTypes.bool.isRequired,
  featureEnabled: PropTypes.bool.isRequired,
  loadingConversations: PropTypes.bool.isRequired,
  loadingMessages: PropTypes.bool.isRequired,
  sendingMessage: PropTypes.bool.isRequired,
  streamingResponse: PropTypes.bool.isRequired,
  conversations: PropTypes.arrayOf(PropTypes.shape({
    slug: PropTypes.string.isRequired,
    title: PropTypes.string,
    last_message_at: PropTypes.string,
    last_message_preview: PropTypes.string,
  })).isRequired,
  messages: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    role: PropTypes.string.isRequired,
    status: PropTypes.string.isRequired,
    content: PropTypes.string,
    created_at: PropTypes.string,
    attachments: PropTypes.arrayOf(PropTypes.shape({
      file_token: PropTypes.string.isRequired,
      original_filename: PropTypes.string.isRequired,
      download_url: PropTypes.string,
      content_type: PropTypes.string,
      byte_size: PropTypes.number,
    })),
    grounding: PropTypes.shape({
      sources: PropTypes.arrayOf(PropTypes.shape({
        title: PropTypes.string,
        url: PropTypes.string,
        domain: PropTypes.string,
      })),
    }),
  })).isRequired,
  selectedConversationSlug: PropTypes.string,
  error: PropTypes.string,
  isNearBottom: PropTypes.bool.isRequired,
  hasUnseenNewMessages: PropTypes.bool.isRequired,
  retryingMessageIds: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  ).isRequired,
  windowPosition: PropTypes.shape({ x: PropTypes.number, y: PropTypes.number }),
  windowSize: PropTypes.shape({ width: PropTypes.number, height: PropTypes.number }).isRequired,
  sidebarCollapsed: PropTypes.bool.isRequired,
  viewerChatVisible: PropTypes.bool,
  cable: PropTypes.object.isRequired,
  setAiChatVisible: PropTypes.func.isRequired,
  setAiChatFeatureEnabled: PropTypes.func.isRequired,
  setAiChatNearBottom: PropTypes.func.isRequired,
  clearAiChatUnseenMessages: PropTypes.func.isRequired,
  selectAiChatConversation: PropTypes.func.isRequired,
  getAiChatConversations: PropTypes.func.isRequired,
  createAiChatConversation: PropTypes.func.isRequired,
  renameAiChatConversation: PropTypes.func.isRequired,
  getAiChatMessages: PropTypes.func.isRequired,
  sendAiChatMessage: PropTypes.func.isRequired,
  retryAiChatMessage: PropTypes.func.isRequired,
  deleteAiChatConversation: PropTypes.func.isRequired,
  applyAiChatStreamDelta: PropTypes.func.isRequired,
  resetAiChatStreamMessage: PropTypes.func.isRequired,
  completeAiChatStreamMessage: PropTypes.func.isRequired,
  failAiChatStreamMessage: PropTypes.func.isRequired,
  setAiChatWindowGeometry: PropTypes.func.isRequired,
  setAiChatSidebarCollapsed: PropTypes.func.isRequired,
  // undefined: question unanswered; null: "ask something else"; object: linked record.
  draftDocument: PropTypes.shape({
    fileToken: PropTypes.string.isRequired,
    title: PropTypes.string,
    viewerPath: PropTypes.string,
  }),
  draftIncludePromptOpen: PropTypes.bool,
  viewedFileToken: PropTypes.string,
  viewedDocumentTitle: PropTypes.string,
  viewedDocumentViewerPath: PropTypes.string,
  startAiChatDraft: PropTypes.func.isRequired,
  setAiChatDraftDocument: PropTypes.func.isRequired,
  openAiChatDraftIncludePrompt: PropTypes.func.isRequired,
  createAiChatDocumentConversation: PropTypes.func.isRequired,
};

AiChatComponent.defaultProps = {
  user: null,
  selectedConversationSlug: null,
  error: null,
  windowPosition: null,
  viewerChatVisible: false,
  draftDocument: undefined,
  draftIncludePromptOpen: false,
  viewedFileToken: null,
  viewedDocumentTitle: null,
  viewedDocumentViewerPath: null,
};

// The record open in a viewer (side panel or full page). Primitives only, so the
// widget re-renders when the record changes rather than on every store update.
const mapViewedDocument = (state) => {
  const { fileToken, documentTitle, isAtaFax } = state.records;
  if (!fileToken || isAtaFax) {
    return { viewedFileToken: null, viewedDocumentTitle: null, viewedDocumentViewerPath: null };
  }

  return {
    viewedFileToken: fileToken,
    viewedDocumentTitle: documentTitle || null,
    viewedDocumentViewerPath: documentViewerPath(selectSelectedRecordDetails(state), fileToken),
  };
};

export default connect((state) => ({
  user: state.auth.data,
  visible: state.aiChat.visible,
  featureEnabled: state.aiChat.featureEnabled,
  loadingConversations: state.aiChat.loadingConversations,
  loadingMessages: state.aiChat.loadingMessages,
  sendingMessage: state.aiChat.sendingMessage,
  streamingResponse: state.aiChat.streamingResponse,
  conversations: state.aiChat.conversations,
  messages: state.aiChat.messages,
  selectedConversationSlug: state.aiChat.selectedConversationSlug,
  isNearBottom: state.aiChat.isNearBottom,
  hasUnseenNewMessages: state.aiChat.hasUnseenNewMessages,
  retryingMessageIds: state.aiChat.retryingMessageIds,
  error: state.aiChat.error,
  windowPosition: state.aiChat.windowPosition,
  windowSize: state.aiChat.windowSize,
  sidebarCollapsed: state.aiChat.sidebarCollapsed,
  viewerChatVisible: state.aiChat.viewerChatVisible,
  draftDocument: state.aiChat.draftDocument,
  draftIncludePromptOpen: state.aiChat.draftIncludePromptOpen,
  ...mapViewedDocument(state),
}), {
  setAiChatVisible,
  setAiChatFeatureEnabled,
  setAiChatNearBottom,
  clearAiChatUnseenMessages,
  selectAiChatConversation,
  getAiChatConversations,
  createAiChatConversation,
  renameAiChatConversation,
  getAiChatMessages,
  sendAiChatMessage,
  retryAiChatMessage,
  deleteAiChatConversation,
  applyAiChatStreamDelta,
  resetAiChatStreamMessage,
  completeAiChatStreamMessage,
  failAiChatStreamMessage,
  setAiChatWindowGeometry,
  setAiChatSidebarCollapsed,
  startAiChatDraft,
  setAiChatDraftDocument,
  openAiChatDraftIncludePrompt,
  createAiChatDocumentConversation,
})(AiChatComponent);
